"use client";
import { useFeedbackSession } from "./use-feedback-session";
import { closeFeedback } from "./active-feedback-session";
import { FeedbackAccessNotice } from "./feedback-access-notice";
import { lazy, Suspense, useEffect } from "react";
import { buildFeedbackHref } from "../page-route";
import { useFeedbackViewer } from "./use-feedback-viewer";
import { buildHref } from "@softmax/web-addresses";

const FeedbackWidget = lazy(() =>
  import("../session/feedback-widget").then((module) => ({
    default: module.FeedbackWidget,
  })),
);
const FeedbackLauncher = lazy(() =>
  import("../launcher/feedback-launcher").then((module) => ({
    default: module.FeedbackLauncher,
  })),
);
export function FeedbackBootstrap({
  launcherEnabled,
}: {
  readonly launcherEnabled?: boolean;
} = {}) {
  const { pathname, search, activeSessionId, pageRoute, isDisabledPath } =
    useFeedbackSession();
  const {
    viewer,
    error: viewerError,
    retry,
    revokeLauncher,
  } = useFeedbackViewer({
    activeSessionId,
    isDisabledPath,
    launcherEnabled,
  });
  useEffect(() => {
    if (!activeSessionId || !viewer || viewer.isAuthenticated) return;

    const currentHref = pathname + (search ? "?" + search : "");
    window.location.assign(
      buildHref({
        name: "signIn",
        search: new URLSearchParams({
          redirect_url: buildFeedbackHref({
            route: currentHref,
            sessionId: activeSessionId,
          }),
        }),
      }),
    );
  }, [activeSessionId, pathname, search, viewer]);

  if (isDisabledPath) return null;

  if (!activeSessionId) {
    const enabled = launcherEnabled ?? viewer?.launcherEnabled;
    return enabled ? (
      <Suspense fallback={null}>
        <FeedbackLauncher onRevoked={revokeLauncher} />
      </Suspense>
    ) : null;
  }

  if (viewerError && !viewer) {
    return (
      <FeedbackAccessNotice
        state={{
          kind: "error",
          message: viewerError,
          onRetry: retry,
        }}
      />
    );
  }

  if (!viewer) return <FeedbackAccessNotice state={{ kind: "loading" }} />;

  if (!viewer.isSoftmaxTeamMember)
    return <FeedbackAccessNotice state={{ kind: "forbidden" }} />;

  if (!viewer.user)
    throw new Error("Authenticated feedback viewer has no user");

  return (
    <>
      {viewerError && (
        <output className="fixed top-4 left-4 z-[85] rounded-xl bg-[rgba(255,243,236,0.98)] p-3 text-sm text-[#b25c3b]">
          Feedback access could not refresh.{" "}
          <button
            type="button"
            onClick={retry}
            className="font-semibold underline"
          >
            Retry
          </button>
        </output>
      )}
      <Suspense fallback={null}>
        <FeedbackWidget
          onClose={closeFeedback}
          pageRoute={pageRoute}
          sessionId={activeSessionId}
          viewerId={viewer.user.id}
        />
      </Suspense>
    </>
  );
}
